import { createFileRoute } from '@tanstack/react-router';
import {
  AlertTriangle,
  Download,
  FileArchive,
  Fingerprint,
  ListOrdered,
  ShieldCheck,
  TerminalSquare,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { CopyButton } from '@/components/ui/CopyButton';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import {
  useClientDownloadInfo,
  useDownloadClientBundle,
} from '@/hooks/useClientDownload';

/**
 * Howto-Seite: Bedienanleitung für das Gesamt-Tool plus Download des
 * Ausführungs-Clients (wt-client.py + README als ZIP-Bundle).
 *
 * Inhaltliche Quelle ist client/wt-client.py bzw. client/README.md - hier
 * steht bewusst NUR, was der Client wirklich tut.
 */

export const Route = createFileRoute('/howto')({
  component: HowtoPage,
});

const TOC = [
  { href: '#konzept', label: 'Konzept: Server plant, Client führt aus' },
  { href: '#ablauf', label: 'Ablauf einer Wartung' },
  { href: '#client', label: 'Ausführungs-Client (wt-client)' },
  { href: '#dry-run', label: 'Dry-Run, Snapshots & Rollback' },
];

function HowtoPage(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Howto</h1>
        <p className="text-muted">
          Bedienanleitung: vom Inventar bis zum abgeschlossenen Wartungslauf.
        </p>
      </header>

      <nav aria-label="Inhalt" className="panel p-4">
        <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-muted">
          <ListOrdered className="size-4" aria-hidden="true" /> Inhalt
        </p>
        <ul className="flex flex-col gap-1 text-sm md:flex-row md:flex-wrap md:gap-x-6">
          {TOC.map((item) => (
            <li key={item.href}>
              <a className="text-primary hover:underline" href={item.href}>
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <KonzeptSection />
      <AblaufSection />
      <ClientSection />
      <DryRunSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} aria-label={title} className="scroll-mt-16">
      <Card title={title}>
        <div className="flex flex-col gap-3 text-sm leading-relaxed">{children}</div>
      </Card>
    </section>
  );
}

/** Codeblock mit Kopieren-Button (CopyButton wie auf der ssh-config-Seite). */
function CodeBlock({ code, label }: { code: string; label?: string }): JSX.Element {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface-2">
      <div className="flex items-center justify-between border-b border-border px-3 py-1">
        <span className="text-xs text-muted">{label ?? 'Shell'}</span>
        <CopyButton value={code} ariaLabel={`${label ?? 'Code'} kopieren`} />
      </div>
      <pre className="overflow-x-auto p-3 text-xs">{code}</pre>
    </div>
  );
}

function Callout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

function H3({ children }: { children: ReactNode }): JSX.Element {
  return <h3 className="mt-2 text-base font-semibold">{children}</h3>;
}

// ---------------------------------------------------------------------------
// 1. Konzept
// ---------------------------------------------------------------------------

function KonzeptSection(): JSX.Element {
  return (
    <Section id="konzept" title="Konzept: Server plant, Client führt aus">
      <p>
        Das Tool ist bewusst zweigeteilt (ADR-011). Der Server unter{' '}
        <code className="rounded bg-surface-2 px-1 text-xs">wartung.itkfm.at</code> ist
        reine Kontrollebene, die Ausführung passiert ausschließlich auf dem Admin-PC.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border border-border p-3">
          <p className="text-xs uppercase tracking-wider text-muted">
            Server (Control Plane)
          </p>
          <ul className="mt-2 list-disc pl-5">
            <li>Inventar, Verträge, Wartungspläne, Fälligkeiten, Drift, Mails, Zabbix</li>
            <li>Plant Runs und stellt sie als Warteschlange bereit</li>
            <li>
              Hat <strong>keinen</strong> SSH-Zugang zu Kundensystemen und keine Secrets
              — Endpunkte, die selbst ausführen würden, lehnen ab
            </li>
          </ul>
        </div>
        <div className="rounded-md border border-border p-3">
          <p className="text-xs uppercase tracking-wider text-muted">
            Client (Admin-PC)
          </p>
          <ul className="mt-2 list-disc pl-5">
            <li>
              <code className="rounded bg-surface-2 px-1 text-xs">wt-client.py</code>{' '}
              holt geplante Runs per HTTPS ab und führt sie mit dem lokalen{' '}
              <code className="rounded bg-surface-2 px-1 text-xs">ansible-playbook</code>{' '}
              aus
            </li>
            <li>Yubikey im lokalen ssh-agent, VPNs lokal, Bitwarden lokal</li>
            <li>Passwörter und BW_SESSION verlassen den Admin-PC nie</li>
          </ul>
        </div>
      </div>
      <p>
        Das ist Absicht, kein Provisorium: selbst bei kompromittiertem Server kommt
        niemand an Kundensysteme — es fehlen Schlüssel, Session und Netzweg.
      </p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 2. Ablauf
// ---------------------------------------------------------------------------

function AblaufSection(): JSX.Element {
  return (
    <Section id="ablauf" title="Ablauf einer Wartung">
      <ol className="flex list-decimal flex-col gap-2 pl-5">
        <li>
          <strong>Systeme &amp; Inventar pflegen:</strong> Kunden und Systeme unter{' '}
          <em>Kunden</em> / <em>Systeme</em>; der <em>Inventar-Abgleich</em> importiert
          und vergleicht den bestehenden itkfm.yml-Bestand.
        </li>
        <li>
          <strong>Drift ansehen:</strong> Die Seite <em>Drift</em> zeigt Abweichungen
          zwischen Soll-Versionen und den beobachteten Versionen der Systeme.
        </li>
        <li>
          <strong>Fälligkeit prüfen:</strong> <em>Fällige Wartungen</em> und{' '}
          <em>Wartungspläne</em> zeigen, was wann dran ist.
        </li>
        <li>
          <strong>Wartung starten:</strong> Routine unter <em>Routinen</em> öffnen,
          Zielsysteme wählen — es entsteht zuerst ein <strong>Dry-Run</strong>. Der Run
          wartet als <code className="rounded bg-surface-2 px-1 text-xs">queued</code>,
          bis ein wt-client ihn abholt.
        </li>
        <li>
          <strong>Client laufen lassen:</strong> Auf dem Admin-PC{' '}
          <code className="rounded bg-surface-2 px-1 text-xs">./wt-client.py once</code>{' '}
          (oder <code className="rounded bg-surface-2 px-1 text-xs">watch</code>) — Logs
          erscheinen live im Run-Detail.
        </li>
        <li>
          <strong>Dry-Run prüfen, Echtlauf bestätigen:</strong> Im Run-Detail den Diff
          ansehen und den Echtlauf bestätigen — Routine und Systeme werden aus dem
          Dry-Run übernommen, der Echtlauf geht wieder in die Warteschlange.
        </li>
        <li>
          <strong>Ergebnis &amp; Mail:</strong> Endstatus im Run-Detail; die
          Run-Ereignis-Mails liegen im <em>Postausgang</em> (automatischer Versand oder
          Entwurf, je nach Mail-Einstellung).
        </li>
      </ol>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 3. Client inkl. Download
// ---------------------------------------------------------------------------

function ClientSection(): JSX.Element {
  return (
    <Section id="client" title="Ausführungs-Client (wt-client)">
      <DownloadCard />

      <H3>Voraussetzungen</H3>
      <ul className="list-disc pl-5">
        <li>Python 3 — der Client nutzt nur die Stdlib, keine pip-Pakete</li>
        <li>
          <code className="rounded bg-surface-2 px-1 text-xs">ansible-playbook</code> im
          PATH (macOS: <code className="rounded bg-surface-2 px-1 text-xs">brew install ansible</code>,
          alternativ <code className="rounded bg-surface-2 px-1 text-xs">pipx install ansible</code>)
        </li>
        <li>
          Laufender ssh-agent mit dem Yubikey (
          <code className="rounded bg-surface-2 px-1 text-xs">SSH_AUTH_SOCK</code> gesetzt)
        </li>
        <li>
          Optional: bw-CLI + BW_SESSION für Passwort-Hosts, Tunnelblick (macOS) bzw.
          nmcli (Linux) für automatisches VPN-Management
        </li>
        <li>
          macOS und Linux; Windows best effort — Login, Claim und Reporting laufen,
          Ansible als Controller braucht dort WSL
        </li>
      </ul>

      <H3>Start</H3>
      <CodeBlock
        label="Erststart"
        code={`unzip wt-client.zip && cd wt-client
./wt-client.py once`}
      />
      <CodeBlock
        label="Dauerbetrieb"
        code={`./wt-client.py watch --interval 10`}
      />
      <p>
        <strong>once</strong> führt genau einen Claim-Zyklus aus und endet — Exit 0 auch
        dann, wenn kein Job wartet (Ausgabe: <em>Kein Job in der Warteschlange</em>).{' '}
        <strong>watch</strong> fragt endlos alle <em>--interval</em> Sekunden (Default
        10) nach Jobs; Strg+C beendet. Das Passwort kommt aus{' '}
        <code className="rounded bg-surface-2 px-1 text-xs">WT_PASSWORD</code> oder wird
        interaktiv abgefragt; bei abgelaufenem Token meldet sich der Client automatisch
        neu an.
      </p>

      <H3>Konfiguration</H3>
      <div className="overflow-x-auto">
        <DataTable
          caption="Konfigurationsschalter"
          columns={HOWTO_CONFIG_COLUMNS}
          rows={HOWTO_CONFIG_ROWS}
          rowKey={(r) => r.env}
          filters={false}
          className="w-full min-w-[32rem] text-left text-xs"
        />
      </div>

      <Callout>
        <strong>Yubikey:</strong> Vor jedem Step zeigt der Client einen Banner — wenn
        der Yubikey blinkt, jetzt berühren. Sonst läuft der Step in den Timeout
        (Default 1800 s pro Step, Vorgabe kommt aus dem Job).
      </Callout>

      <H3>Bitwarden (Passwort-Hosts)</H3>
      <p>
        Der Server kennt keine Secrets. Der Client ergänzt das Inventar nach dem Claim
        lokal aus der bw-map (TSV:{' '}
        <code className="rounded bg-surface-2 px-1 text-xs">
          host&lt;TAB&gt;item_id[&lt;TAB&gt;user]
        </code>
        ) um Bitwarden-Lookup-Templates — das Passwort holt Ansible zur Laufzeit selbst.
        Der Server darf zusätzlich die Bitwarden-<em>Item-ID</em> pro System mitliefern
        (zentrale Pflege); aufgelöst wird immer nur lokal, eine lokale bw-map-Zeile
        überschreibt den Server-Wert. Ohne BW_SESSION warnt der Client nur —
        Key-basierte Hosts laufen normal weiter.
      </p>
      <CodeBlock label="Bitwarden entsperren" code={`export BW_SESSION="$(bw unlock --raw)"`} />

      <H3>VPN (WT_VPN_MODE)</H3>
      <ul className="list-disc pl-5">
        <li>
          <strong>warn</strong> (Default): vor betroffenen Steps wird geprüft, ob ein
          tun/utun-Interface aktiv ist — sonst Warnung, kein Abbruch.
        </li>
        <li>
          <strong>manage</strong>: baut benötigte VPNs vor den Steps selbst auf
          (Tunnelblick bzw. nmcli), wartet bis 45 s auf das Interface und trennt nach
          dem Run nur die selbst aufgebauten Verbindungen (
          <code className="rounded bg-surface-2 px-1 text-xs">WT_VPN_KEEP=1</code> lässt
          sie offen).
        </li>
        <li>
          <strong>off</strong>: keine VPN-Prüfungen. Die optionale vpn-map (TSV:{' '}
          <code className="rounded bg-surface-2 px-1 text-xs">
            vpn_ref&lt;TAB&gt;verbindungsname
          </code>
          ) mappt Server-Referenzen auf lokale Verbindungsnamen; ohne Eintrag gilt der
          Dateiname ohne .ovpn-Endung.
        </li>
      </ul>

      <H3>Fehlersuche</H3>
      <ul className="list-disc pl-5">
        <li>
          <em>Login fehlgeschlagen: ungültige Anmeldedaten</em> — WT_USERNAME /
          WT_PASSWORD prüfen (gleiche Zugangsdaten wie im WebUI).
        </li>
        <li>
          <em>ansible-playbook wurde nicht gefunden</em> — Ansible installieren (siehe
          Voraussetzungen); unter Windows in WSL.
        </li>
        <li>
          <em>SSH_AUTH_SOCK ist nicht gesetzt</em> — ssh-agent mit dem Yubikey starten,
          sonst schlagen die SSH-Verbindungen fehl.
        </li>
        <li>
          <em>VPN … vermutlich nicht verbunden</em> — VPN manuell aufbauen oder{' '}
          <code className="rounded bg-surface-2 px-1 text-xs">WT_VPN_MODE=manage</code>{' '}
          nutzen.
        </li>
        <li>
          <em>BW_SESSION ist nicht gesetzt</em> — bw entsperren (Kommando oben), sonst
          werden keine Bitwarden-Zugangsdaten injiziert.
        </li>
        <li>
          <em>Timeout nach 1800 s</em> — meist wurde der Yubikey beim Step-Banner nicht
          berührt.
        </li>
        <li>
          <em>Kein Job in der Warteschlange</em> — kein Fehler: erst im WebUI einen Run
          starten, dann claimen.
        </li>
      </ul>
    </Section>
  );
}

interface HowtoConfigRow {
  flag: string;
  env: string;
  def: string;
}

const HOWTO_CONFIG_ROWS: readonly HowtoConfigRow[] = [
  { flag: '--server', env: 'WT_SERVER', def: 'https://wartung.itkfm.at' },
  { flag: '--username', env: 'WT_USERNAME', def: 'admin' },
  { flag: '(Passwort)', env: 'WT_PASSWORD', def: 'interaktiver Prompt' },
  {
    flag: '(Config-Dir)',
    env: 'WT_CONFIG_DIR',
    def: '~/.config/wt-client (Windows: %APPDATA%\\wt-client)',
  },
  { flag: '(bw-map)', env: 'WT_BW_MAP', def: '<config-dir>/bw-map.tsv' },
  { flag: '(vpn-map)', env: 'WT_VPN_MAP', def: '<config-dir>/vpn-map.tsv' },
  { flag: '(VPN-Modus)', env: 'WT_VPN_MODE', def: 'warn (off | warn | manage)' },
  {
    flag: '(VPN halten)',
    env: 'WT_VPN_KEEP',
    def: 'unset (1 = VPNs nach dem Run offen lassen)',
  },
];

const HOWTO_CONFIG_COLUMNS: ReadonlyArray<Column<HowtoConfigRow>> = [
  {
    id: 'flag',
    header: 'Flag',
    className: 'py-1.5 pr-3 font-mono',
    cell: (r) => r.flag,
  },
  {
    id: 'env',
    header: 'ENV',
    className: 'py-1.5 pr-3 font-mono',
    cell: (r) => r.env,
  },
  {
    id: 'default',
    header: 'Default',
    className: 'py-1.5',
    cell: (r) => r.def,
  },
];

/** Download-Karte: ZIP-Bundle + SHA256 aus GET /client/download/info. */
function DownloadCard(): JSX.Element {
  const info = useClientDownloadInfo();
  const download = useDownloadClientBundle();
  const script = info.data?.files.find((f) => f.filename === 'wt-client.py');

  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileArchive className="size-8 text-primary" aria-hidden="true" />
          <div>
            <p className="font-semibold">wt-client herunterladen</p>
            <p className="text-xs text-muted">
              ZIP-Bundle: wt-client.py + README.md — Bedienanleitung liegt gleich bei.
            </p>
          </div>
        </div>
        <Button
          leftIcon={<Download className="size-4" />}
          onClick={() => download.mutate()}
          loading={download.isPending}
        >
          Bundle herunterladen (.zip)
        </Button>
      </div>

      {download.isError && <ErrorAlert className="mt-3" error={download.error} />}
      {info.isError && <ErrorAlert className="mt-3" error={info.error} />}

      {info.data && (
        <div className="mt-3 flex flex-col gap-1 border-t border-primary/20 pt-3 text-xs">
          <p className="flex items-center gap-1.5 text-muted">
            <Fingerprint className="size-3.5" aria-hidden="true" />
            SHA256 zur Integritätsprüfung nach dem Download:
          </p>
          {script && <ChecksumRow label="wt-client.py" sha256={script.sha256} />}
          <ChecksumRow label={info.data.bundle_filename} sha256={info.data.bundle_sha256} />
          <div className="mt-2">
            <CodeBlock
              label="Prüfen (macOS; Linux: sha256sum)"
              code={`shasum -a 256 wt-client.py`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ChecksumRow({ label, sha256 }: { label: string; sha256: string }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-28 shrink-0 font-medium">{label}</span>
      <code className="break-all rounded bg-surface-2 px-1.5 py-0.5 font-mono">{sha256}</code>
      <CopyButton value={sha256} ariaLabel={`SHA256 von ${label} kopieren`} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Dry-Run, Snapshots & Rollback
// ---------------------------------------------------------------------------

function DryRunSection(): JSX.Element {
  return (
    <Section id="dry-run" title="Dry-Run, Snapshots & Rollback">
      <p className="flex items-start gap-2">
        <TerminalSquare className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
        <span>
          <strong>Dry-Run</strong> = <code className="rounded bg-surface-2 px-1 text-xs">
            ansible-playbook --check
          </code>
          : verbindet sich mit den Systemen, ändert aber nichts und zeigt, was ein
          Echtlauf ändern würde (Diff im Run-Detail). Die Garantie gilt, soweit die
          Routine Check-Mode unterstützt — das ist als Metadatum an der Routine
          hinterlegt.
        </span>
      </p>
      <p className="flex items-start gap-2">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
        <span>
          <strong>Snapshots:</strong> Routinen mit Snapshot-Pflicht (destruktive
          Routinen immer) verlangen vor dem Echtlauf einen Proxmox-Snapshot je
          Zielsystem — möglich nur für VMs mit vmid. Angelegte Snapshots sind im
          Run-Detail einsehbar. <strong>Rollback:</strong> Im Run-Detail spielt der
          Rollback-Button den Pre-Run-Snapshot zurück, der Run erhält den Status{' '}
          <code className="rounded bg-surface-2 px-1 text-xs">rolled_back</code>.
        </span>
      </p>
      <Callout>
        Im Client-Betrieb lehnt der Server snapshot-pflichtige <em>Echtläufe</em>{' '}
        derzeit ab — die Kontrollebene hat keinen Snapshot-Zugriff (ADR-011). Dry-Runs
        sind davon nicht betroffen.
      </Callout>
    </Section>
  );
}
